import { Skeleton } from "@/common/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/common/ui/table";

import { formatTokenCount } from "./format";
import type { UserMetrics } from "./types";

interface TopUsersTableProps {
  users: UserMetrics[];
  isLoading: boolean;
}

const SKELETON_ROWS = 5;

export function TopUsersTable({ users, isLoading }: TopUsersTableProps): React.ReactElement {
  return (
    <div data-testid="top-users-table">
      <h2 className="mb-3 text-lg font-semibold tracking-tight">Top Users</h2>
      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User ID</TableHead>
              <TableHead className="text-right">Requests</TableHead>
              <TableHead className="text-right">Est. Input Tokens</TableHead>
              <TableHead className="text-right">Est. Output Tokens</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <TableRow key={i} data-testid={`user-skeleton-${i}`}>
                    <TableCell>
                      <Skeleton className="h-5 w-40" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-5 w-12" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-5 w-16" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="ml-auto h-5 w-16" />
                    </TableCell>
                  </TableRow>
                ))
              : users.map((user) => (
                  <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                    <TableCell className="font-mono text-sm">{user.user_id}</TableCell>
                    <TableCell className="text-right">
                      {user.request_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatTokenCount(user.input_tokens)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatTokenCount(user.output_tokens)}
                    </TableCell>
                  </TableRow>
                ))}
            {!isLoading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                  No usage data for this time range.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
